import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Home as HomeIcon,
  Car,
  Wallet,
  Briefcase,
  Shield,
  Users,
  Clock,
  MapPin,
  CheckCircle2,
} from "lucide-react";
import { BUSINESS } from "../lib/api";
import { SEO } from "../components/SEO";

const HERO_IMG =
  "https://images.unsplash.com/photo-1632482590163-5f7e91277173?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80";

const services = [
  {
    id: "home-loans",
    title: "Home Loans",
    desc: "First home buyers, refinances, investment loans. Access to 40+ lenders.",
    icon: HomeIcon,
    img: "https://images.unsplash.com/photo-1666000346172-8e7e87547d2a?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  },
  {
    id: "car-loans",
    title: "Car & Asset Finance",
    desc: "Competitive rates on cars, utes, machinery & equipment.",
    icon: Car,
    img: "https://images.unsplash.com/photo-1774740770881-fba338a79565?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  },
  {
    id: "personal-loans",
    title: "Personal Loans",
    desc: "Renovations, weddings, consolidation — clear, honest lending.",
    icon: Wallet,
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  },
  {
    id: "business-loans",
    title: "Business & Commercial",
    desc: "Working capital, commercial property, cash-flow lending.",
    icon: Briefcase,
    img: "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  },
];

const trust = [
  { icon: Shield, title: "Licensed & accredited", desc: "Fully accredited under Australian Credit License. Compliant, transparent, always." },
  { icon: Users, title: "40+ lenders", desc: "We shop the whole market so you don’t have to. One application, many options." },
  { icon: Clock, title: "Fast, honest answers", desc: "Real people, real callbacks. Same-day pre-assessments on most enquiries." },
  { icon: MapPin, title: "Local to Craigieburn", desc: "Face-to-face meetings in Craigieburn, or Zoom Australia-wide." },
];

export default function Home() {
  return (
    <div>
      <SEO
        path="/"
        title="Australia-wide Finance & Mortgage Brokers"
        description="Sixty Finance — Australia-wide finance broker. Home loans, car & asset finance, personal & business lending. Compare 40+ lenders. Call 0431 550 780."
        keywords="finance broker Australia, mortgage broker, home loan broker, car finance, asset finance, business loans, Craigieburn broker"
      />
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden hero-mesh text-white"
      >
        <img
          src={HERO_IMG}
          alt="Melbourne skyline"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/85 via-emerald-950/70 to-emerald-900/60" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 md:pt-28 pb-24 md:pb-32">
          <div className="max-w-3xl fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-xs font-semibold tracking-wider uppercase text-emerald-200 border border-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Always by your side
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-white">
              The right loan,{" "}
              <span className="text-emerald-300">without the runaround.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-emerald-50/85 max-w-2xl leading-relaxed">
              Home, car, personal & business finance — brokered by real people
              all over Australia. We compare 40+ lenders to find you a better
              deal, and stay with you until settlement.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/quote"
                data-testid="hero-quote-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-emerald-400 text-emerald-950 font-semibold hover:bg-emerald-300 hover:scale-105 transition-transform duration-200"
              >
                Get my free quote
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white font-semibold hover:bg-white/20 transition-colors duration-200"
              >
                <Phone className="h-4 w-4" />
                <span className="num">{BUSINESS.phone}</span>
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 text-sm text-emerald-100/80">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> No obligation</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Won’t affect your credit score</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Same-day callback</span>
            </div>
          </div>

          {/* Floating card */}
          <div className="hidden lg:block absolute right-8 bottom-8 w-[320px] rounded-2xl bg-white/95 backdrop-blur p-6 shadow-2xl fade-up fade-up-delay-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl overflow-hidden ring-1 ring-emerald-900/10 shadow-sm">
                <img src={BUSINESS.logoUrl} alt="Sixty Finance" className="h-full w-full object-cover" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Talk to a broker today</p>
                <p className="text-emerald-950 font-semibold text-sm leading-tight italic">Always by your side.</p>
              </div>
            </div>
            <a
              href={BUSINESS.phoneHref}
              className="mt-5 inline-flex items-center gap-2 text-emerald-800 font-bold num text-xl hover:text-emerald-600"
            >
              <Phone className="h-5 w-5" /> {BUSINESS.phone}
            </a>
            <p className="mt-3 text-xs text-slate-500">{BUSINESS.hours}</p>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE SIXTY FINANCE */}
      <section
        data-testid="why-choose-section"
        className="max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-10 md:pt-28 md:pb-14"
      >
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
              Why choose us
            </p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-emerald-950 leading-[1.1]">
              Why choose <span className="text-emerald-600">Sixty Finance</span>.
            </h2>
            <div className="mt-8 hidden lg:block">
              <div className="h-1 w-16 rounded-full bg-emerald-500" />
              <p className="mt-6 text-sm text-emerald-800/80 italic">
                Always by your side — from first enquiry to settlement and beyond.
              </p>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-5 text-slate-700 leading-relaxed">
            <p>
              At Sixty Finance, we believe securing the right finance should be
              straightforward, transparent, and tailored to your individual needs.
              Whether you're purchasing your first home, refinancing an existing
              loan, buying a vehicle, investing in property, or seeking business
              finance, we're committed to finding a solution that works for
              you — not just what's easiest.
            </p>
            <p>
              Unlike dealing directly with a single lender, we compare a wide
              range of lending options to help you access competitive rates and
              loan products that align with your financial goals. We take the
              time to understand your circumstances, explain your options in
              plain language, and guide you through every step of the process
              with honest advice and ongoing support.
            </p>
            <p>
              Our focus is on building lasting relationships through trust,
              reliability, and exceptional customer service. We know that every
              client's journey is different, which is why we provide personalised
              guidance from your initial enquiry through to settlement and
              beyond.
            </p>
            <p>
              At Sixty Finance, you're more than just another application.
              You're a valued client, and your success is our priority. Let us
              take the stress out of finance so you can focus on what matters
              most, with the confidence that you have a dedicated finance
              partner by your side every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        data-testid="services-section"
        className="max-w-7xl mx-auto px-4 md:px-8 pt-4 pb-20 md:pt-6 md:pb-28"
      >
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">What we do</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-emerald-950">
            Finance for every stage of your life.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Whether you’re buying your first home or expanding a business, we
            match you to a lender who’ll actually say yes — at a rate that
            makes sense.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.id}
                to={`/services#${s.id}`}
                data-testid={`service-card-${s.id}`}
                className={`group relative overflow-hidden rounded-2xl bg-white border border-gray-100 p-6 hover:-translate-y-1 hover:shadow-xl transition-transform duration-300 fade-up fade-up-delay-${i + 1}`}
              >
                <div className="h-32 -m-6 mb-6 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-emerald-50 text-emerald-800">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-emerald-950">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 group-hover:text-emerald-500">
                  Learn more <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* TRUST / WHY US - Bento */}
      <section
        data-testid="trust-section"
        className="bg-emerald-950 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #10b981 0, transparent 40%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-5">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                A better broker starts with better questions.
              </h2>
              <p className="mt-4 text-emerald-100/80 leading-relaxed max-w-md">
                We take the time to understand you first — then find the lender
                that fits. No pressure, no upsell, no jargon.
              </p>
              <Link
                to="/about"
                data-testid="trust-about-link"
                className="mt-6 inline-flex items-center gap-2 text-emerald-300 font-semibold hover:text-white transition-colors"
              >
                Our story <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
              {trust.map((t) => {
                const Icon = t.icon;
                return (
                  <div
                    key={t.title}
                    className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6 hover:bg-white/[0.07] transition-colors"
                  >
                    <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-emerald-400/20 text-emerald-300">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-semibold text-white">{t.title}</h3>
                    <p className="mt-1.5 text-sm text-emerald-100/80 leading-relaxed">{t.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        data-testid="process-section"
        className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28"
      >
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">The process</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-emerald-950">
              Three simple steps to a smarter loan.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Tell us your goals", d: "5-minute chat or online form. We listen first." },
              { n: "02", t: "We shop the market", d: "40+ lenders compared. You see the numbers." },
              { n: "03", t: "Settle & save", d: "We handle paperwork end-to-end and check in for years." },
            ].map((s) => (
              <div
                key={s.n}
                className="relative rounded-2xl border border-emerald-100 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-transform duration-300"
              >
                <div className="num text-emerald-500 font-semibold text-sm">{s.n}</div>
                <h3 className="mt-3 text-lg font-semibold text-emerald-950">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="faq-section"
        className="bg-white border-y border-gray-100"
      >
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-20 md:py-24">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">FAQ</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-emerald-950">
              Straight answers to common questions.
            </h2>
          </div>
          <dl className="mt-12 divide-y divide-emerald-100">
            {[
              {
                q: "What does a finance broker do?",
                a: "A finance broker compares loan products from many lenders on your behalf, helps package your application, negotiates the sharpest rate, and manages the process end-to-end. At Sixty Finance we have access to 40+ lenders across home, car, personal and business finance — and it costs you nothing to use us, lenders pay us."
              },
              {
                q: "How do I get a car or asset loan through Sixty Finance?",
                a: "Submit a free quote or call 0431 550 780. We'll take a quick summary of what you're financing, your employment situation, and rough loan amount — then compare consumer and business lenders including chattel mortgages, novated leases and equipment finance. Same-day pre-approvals are available on most enquiries."
              },
              {
                q: "Do you service all of Australia?",
                a: "Yes. Sixty Finance is an Australia-wide broking firm. Our head office is in Craigieburn, Victoria, and we have brokers based in Melbourne, Sydney and Adelaide, servicing clients across every state via phone and video."
              },
              {
                q: "Does getting a quote affect my credit score?",
                a: "No. Requesting a quote or pre-assessment from Sixty Finance is a no-obligation conversation and does not touch your credit file. A formal credit enquiry only happens once you choose to submit a full application to a lender."
              },
              {
                q: "How much can I borrow?",
                a: "Borrowing capacity depends on your income, expenses, existing debts, deposit and the lender's policy. On a quick 15-minute call our broker can give you a realistic borrowing range and show you 2–3 lender options that fit your profile."
              },
              {
                q: "How do I contact Sixty Finance?",
                a: "Call 0431 550 780 Monday–Friday 9am–5pm, email dilraj@sixtyfinance.com, or submit a quote request. Head office: 5/10 Nova Court, Craigieburn VIC 3064."
              },
            ].map(({ q, a }) => (
              <details key={q} className="group py-5">
                <summary className="cursor-pointer flex items-start justify-between gap-4 text-left list-none">
                  <dt className="text-base md:text-lg font-semibold text-emerald-950">
                    {q}
                  </dt>
                  <span className="mt-1 inline-flex items-center justify-center h-6 w-6 rounded-full border border-emerald-200 text-emerald-700 group-open:rotate-45 transition-transform duration-300 flex-none">
                    <span className="text-lg leading-none">+</span>
                  </span>
                </summary>
                <dd className="mt-3 text-sm md:text-base text-slate-700 leading-relaxed">
                  {a}
                </dd>
              </details>
            ))}
          </dl>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="cta-section"
        className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28"
      >
        <div className="relative overflow-hidden rounded-3xl bg-emerald-900 p-10 md:p-16 text-white">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Ready to talk numbers?
            </h2>
            <p className="mt-4 text-emerald-100/85 text-base md:text-lg leading-relaxed">
              Grab a 15-minute chat with a broker. Free, no obligation — just
              honest answers about what you can borrow and how.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={BUSINESS.phoneHref}
                data-testid="cta-call-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-emerald-400 text-emerald-950 font-semibold hover:bg-emerald-300 hover:scale-105 transition-transform duration-200"
              >
                <Phone className="h-4 w-4" />
                <span className="num">Call {BUSINESS.phone}</span>
              </a>
              <Link
                to="/quote"
                data-testid="cta-quote-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/10 backdrop-blur border border-white/20 font-semibold hover:bg-white/20 transition-colors"
              >
                Get my free quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
